/**
 * Listing text hygiene (P25009-486 BR-4 / BR-6).
 *
 * Titles, tyre types and tread depths reach the page exactly as a seller,
 * an importer or a title builder wrote them. Three kinds of mistake were
 * visible to buyers: a doubled inch mark ("18"" "), raw enum keys
 * ("winterFriction") where a label belongs, and impossible tread depths
 * ("7887mm" — a typo for 7–8 mm). These helpers clean what can be cleaned
 * and refuse to show what cannot be trusted. Nothing here rewrites stored
 * data.
 */

/* An inch mark doubled after a number: 18"" / 18 "" / 18”” / 18″″. The title
 * builders append '"' to a diameter that sometimes already ends in one. A
 * quote that opens a word (18" "Atom") is a quotation, not an inch mark. */
const DOUBLE_INCH_RE = /(\d)\s*["”″](?:\s*["”″])+(?![A-Za-zÅÄÖåäöÉé0-9])/g;

/**
 * The title as it should read: one inch mark, single spaces, trimmed.
 *
 * @param {string} title raw listing title
 * @returns {string}
 */
export const cleanListingTitle = title =>
  String(title || '')
    .replace(DOUBLE_INCH_RE, '$1"')
    .replace(/\s{2,}/g, ' ')
    .trim();

/* Titles that are notes to the seller's staff, not a product: "RÖR EJ!!!",
 * "Ej till salu", "TEST". They never belong in a recommendation. */
const JUNK_TITLE_RES = [/!{3,}/, /\br[öo]r\s+ej\b/i, /\bej\s+till\s+salu\b/i, /^\s*test\b/i];

/** True when a title reads as an internal note rather than a product. */
export const isJunkTitle = title => {
  const text = String(title || '').trim();
  return !text || JUNK_TITLE_RES.some(re => re.test(text));
};

/* Labels for tyre-type keys that are not (or not yet) options in the hosted
 * field config — seen on imported and legacy listings. */
const TYRE_TYPE_LABELS = {
  summer: 'Sommar',
  winter: 'Vinter',
  winterFriction: 'Vinter friktion',
  winterDubbed: 'Vinter dubbat',
  winterStudded: 'Vinter dubbat',
  allSeason: 'Helår',
  allseason: 'Helår',
  rdack: 'R-däck',
  cdack: 'C-däck',
};

/* "winterFriction", "all_season" — a code, not a word a buyer should read. */
const looksLikeKey = value => /^[a-z]+([A-Z][a-z0-9]*)+$|^[a-z0-9]+_[a-z0-9_]+$/.test(String(value));

/**
 * A value for display: the configured label, else a known label for a raw
 * key, else the raw value when it reads as a word — and null when it is a
 * code nobody can read (BR-6: never "winterFriction" on a card).
 *
 * @param {*} raw the stored value
 * @param {string|null} configLabel the hosted config's label, when one matched
 * @returns {string|null}
 */
export const displayValue = (raw, configLabel = null) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (configLabel) return configLabel;
  const text = String(raw);
  if (TYRE_TYPE_LABELS[text]) return TYRE_TYPE_LABELS[text];
  return looksLikeKey(text) ? null : text;
};

/* A tyre is 7–9 mm new and 1.6 mm at the legal minimum; anything outside
 * 0–20 mm is a typo ("7887" for 7–8 mm), not a measurement. */
const MAX_TREAD_MM = 20;

/**
 * Tread depth in mm when it is a believable number, else null.
 *
 * @param {*} raw stored threaddepth ("7,5", "8 mm", 7887)
 * @returns {number|null}
 */
export const plausibleTreadMm = raw => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n > 0 && n <= MAX_TREAD_MM ? n : null;
};
